import { EngagedIndicesTracker } from './EngagedIndicesTracker';
import {
  type RenderStackEntry,
  RenderStackManager,
} from './RenderStackManager';
import { GridLayoutManager } from './layout-managers/GridLayoutManager';
import type { LayoutManager } from './layout-managers/LayoutManager';
import { LinearLayoutManager } from './layout-managers/LinearLayoutManager';
import { MasonryLayoutManager } from './layout-managers/MasonryLayoutManager';
import type { ItemTypeKey } from './utils/MultiTypeAverageWindow';
import {
  type ViewabilityBinding,
  ViewabilityManager,
} from './viewability/ViewabilityManager';

export type { ViewabilityBinding };

export interface RecyclerViewManagerOptions {
  defaultItemSize?: number;
  drawDistance?: number;
  maxItemsInRecyclePool?: number;
  numColumns?: number;
  horizontal?: boolean;
  masonry?: boolean;
  optimizeItemArrangement?: boolean;
}

const DEFAULT_KEY_EXTRACTOR = (_: unknown, index: number) => String(index);

export class RecyclerViewManager<T> {
  readonly layoutManager: LayoutManager;
  readonly tracker: EngagedIndicesTracker;
  readonly renderStack: RenderStackManager;
  readonly viewability: ViewabilityManager<T>;

  private currentData: ReadonlyArray<T> = [];
  private currentKeyExtractor: (item: T, index: number) => string =
    DEFAULT_KEY_EXTRACTOR;
  private currentItemType?: (item: T, index: number) => ItemTypeKey;
  private currentOverrideItemLayout?: (
    layout: { span?: number },
    item: T,
    index: number,
    maxColumns: number,
  ) => void;
  private currentViewabilityBinding: ViewabilityBinding<T> = {};

  constructor(options: RecyclerViewManagerOptions = {}) {
    const numColumns = options.numColumns ?? 1;
    const horizontal = options.horizontal ?? false;
    const masonry = options.masonry ?? false;
    const getItemType = (index: number) => this.itemTypeAt(index);

    if (masonry && horizontal) {
      throw new Error(
        'FlashList: masonry + horizontal is not supported. Pick one.',
      );
    }

    const overrideItemLayoutAdapter = (
      layout: { span?: number },
      index: number,
      maxColumns: number,
    ) => {
      if (!this.currentOverrideItemLayout) return;
      const item = this.currentData[index];
      if (item === undefined) return;
      this.currentOverrideItemLayout(layout, item, index, maxColumns);
    };

    if (masonry) {
      this.layoutManager = new MasonryLayoutManager({
        windowSize: { width: 0, height: 0 },
        defaultItemSize: options.defaultItemSize,
        getItemType,
        numColumns: Math.max(2, numColumns),
        optimizeItemArrangement: options.optimizeItemArrangement,
        overrideItemLayout: overrideItemLayoutAdapter,
      });
    } else if (numColumns >= 2) {
      this.layoutManager = new GridLayoutManager({
        windowSize: { width: 0, height: 0 },
        defaultItemSize: options.defaultItemSize,
        getItemType,
        numColumns,
        horizontal,
        overrideItemLayout: overrideItemLayoutAdapter,
      });
    } else {
      this.layoutManager = new LinearLayoutManager({
        windowSize: { width: 0, height: 0 },
        defaultItemSize: options.defaultItemSize,
        getItemType,
        horizontal,
      });
    }

    this.tracker = new EngagedIndicesTracker({
      layoutManager: this.layoutManager,
      drawDistance: options.drawDistance,
    });
    this.renderStack = new RenderStackManager({
      maxItemsInRecyclePool: options.maxItemsInRecyclePool,
    });
    this.viewability = new ViewabilityManager<T>({
      getData: () => this.currentData,
      getKey: index => this.keyAt(index),
    });
  }

  /**
   * Swap the active viewability binding. The engine calls this on every
   * render so the latest `onViewableItemsChanged` / `viewabilityConfig`
   * prop identity is reflected — the helper only rebuilds when one of those
   * actually changed.
   */
  setViewability(binding: ViewabilityBinding<T>): void {
    const previous = this.currentViewabilityBinding;
    if (
      previous.onViewableItemsChanged === binding.onViewableItemsChanged &&
      previous.viewabilityConfig === binding.viewabilityConfig
    ) {
      return;
    }
    this.currentViewabilityBinding = binding;
    this.viewability.rebind(binding);
  }

  setData(
    data: ReadonlyArray<T>,
    keyExtractor?: (item: T, index: number) => string,
    getItemType?: (item: T, index: number) => ItemTypeKey,
    overrideItemLayout?: (
      layout: { span?: number },
      item: T,
      index: number,
      maxColumns: number,
    ) => void,
  ): void {
    const lengthChanged = data.length !== this.currentData.length;
    this.currentData = data;
    this.currentKeyExtractor = keyExtractor ?? DEFAULT_KEY_EXTRACTOR;
    this.currentItemType = getItemType;
    this.currentOverrideItemLayout = overrideItemLayout;
    if (lengthChanged) {
      this.layoutManager.setItemCount(data.length);
    }
  }

  setWindowSize(size: { width: number; height: number }): void {
    this.layoutManager.setWindowSize(size);
  }

  setDrawDistance(drawDistance: number): void {
    this.tracker.setDrawDistance(drawDistance);
  }

  get itemCount(): number {
    return this.currentData.length;
  }

  get data(): ReadonlyArray<T> {
    return this.currentData;
  }

  keyAt(index: number): string {
    return this.currentKeyExtractor(this.currentData[index], index);
  }

  itemTypeAt(index: number): ItemTypeKey {
    if (!this.currentItemType) return 'default';
    const item = this.currentData[index];
    if (item === undefined) return 'default';
    return this.currentItemType(item, index);
  }

  indexOfItem(item: T): number {
    return this.currentData.indexOf(item);
  }

  /**
   * Recompute the render stack for the given scroll position + viewport.
   * Returns the new stack and the engaged range so callers can decide
   * whether to trigger a re-render (skip if the range is unchanged).
   */
  computeRenderStack(
    scrollOffset: number,
    viewport: number,
  ): {
    stack: RenderStackEntry[];
    range: { start: number; end: number } | null;
  } {
    const range = this.tracker.computeEngagedRange(scrollOffset, viewport);
    const stack = this.renderStack.sync({
      engagedRange: range,
      itemCount: this.itemCount,
      getStableId: index => this.keyAt(index),
      getItemType: index => this.itemTypeAt(index),
    });
    return { stack, range };
  }

  /**
   * Run the viewability pipeline against the strict viewport (no overscan).
   * The engine calls this from the scroll handler and after data updates.
   * No-ops when no `onViewableItemsChanged` callback is bound.
   */
  computeItemViewability(scrollOffset: number, viewport: number): void {
    if (!this.viewability.shouldListenToVisibleIndices) return;
    if (viewport === 0) return;
    const isHorizontal = this.layoutManager.isHorizontal();
    const range = this.layoutManager.findIndicesInRange(
      scrollOffset,
      scrollOffset + viewport,
    );
    const possiblyViewable: number[] = [];
    if (range) {
      for (let index = range.start; index <= range.end; index++) {
        possiblyViewable.push(index);
      }
    }
    this.viewability.updateViewableItems({
      horizontal: isHorizontal,
      scrollOffset,
      listSize: this.layoutManager.getWindowSize(),
      getLayout: index =>
        index < this.itemCount
          ? this.layoutManager.getLayout(index)
          : undefined,
      possiblyViewableIndices: possiblyViewable,
    });
  }
}
